/**
 * Tree-sitter Native Semantic Query Engine
 * 
 * Uses Tree-sitter's parsed AST structure for semantic code understanding.
 * Language-agnostic design that works with any Tree-sitter grammar.
 * Replaces token-based matching with true AST navigation.
 */

// Tree-sitter query types
export interface QueryCapture {
  name: string;
  node: TreeSitterNode;
}

export interface QueryMatch {
  pattern: number;
  captures: QueryCapture[];
}

export interface TreeSitterNode {
  type: string;
  startPosition: { row: number; column: number };
  endPosition: { row: number; column: number };
  startIndex: number;
  endIndex: number;
  text: string;
  children: TreeSitterNode[];
  parent?: TreeSitterNode;
  childForFieldName?(fieldName: string): TreeSitterNode | null;
  walk(): TreeCursor;
}

export interface TreeCursor {
  currentNode: TreeSitterNode;
  gotoFirstChild(): boolean;
  gotoNextSibling(): boolean;
  gotoParent(): boolean;
}

export interface TreeSitterQuery {
  matches(node: TreeSitterNode): QueryMatch[];
  captures(node: TreeSitterNode): QueryCapture[];
}

/**
 * Semantic context types for LLM selections
 */
export type SemanticContext = 
  | 'function-signature'
  | 'method-call' 
  | 'return-expression'
  | 'field-definition'
  | 'struct-initialization'
  | 'struct-closing'
  | 'comment'
  | 'variable-declaration'
  | 'unknown';

/**
 * AST-based match result using Tree-sitter semantic understanding
 */
export interface SemanticMatch {
  node: TreeSitterNode;
  nodeType: string; // Tree-sitter node type (e.g., 'call_expression', 'function_item')
  context: SemanticContext;
  confidence: number;
  completeExpression: boolean;
  textCoverage: number; // Percentage of target text covered
  semanticRelevance: number; // How well it matches the pedagogical intent
  astTraversal: string; // How the node was found (e.g., 'direct', 'parent', 'child')
}

/**
 * Language-agnostic AST node type mapping
 */
export interface ASTNodeTypeMap {
  functionDeclaration: string[];
  methodCall: string[];
  fieldAccess: string[];
  returnStatement: string[];
  comment: string[];
  variable: string[];
  structInit: string[];
}

/**
 * Tree-sitter node type mappings for different languages
 */
export class ASTNodeTypes {
  static readonly RUST: ASTNodeTypeMap = {
    functionDeclaration: ['function_item'],
    methodCall: ['call_expression'],
    fieldAccess: ['field_expression', 'field_init'],
    returnStatement: ['return_expression'],
    comment: ['line_comment', 'block_comment'],
    variable: ['identifier'],
    structInit: ['struct_expression']
  };

  // Could add other languages like:
  // static readonly JAVASCRIPT: ASTNodeTypeMap = { ... };
  // static readonly PYTHON: ASTNodeTypeMap = { ... };
}

/**
 * Language-agnostic semantic intent classifier
 */
export class SemanticIntentClassifier {
  /**
   * Classify LLM selection by pedagogical intent using language-agnostic heuristics
   */
  static classifyIntent(
    llmText: string, 
    reason: string
  ): {
    primaryIntent: SemanticContext;
    nodeTypesToSearch: string[];
    searchStrategies: string[];
  } {
    const text = llmText.toLowerCase();
    const reasonLower = reason.toLowerCase();

    // Function/method definitions
    if (text.includes('fn ') || reasonLower.includes('function') || reasonLower.includes('signature')) {
      return {
        primaryIntent: 'function-signature',
        nodeTypesToSearch: ['function_item', 'function_declaration', 'method_definition'],
        searchStrategies: ['exact_node', 'span_expansion', 'text_fallback']
      };
    }

    // Method calls (language-agnostic detection)
    if (text.includes('::') || text.includes('.') || reasonLower.includes('call') || reasonLower.includes('method')) {
      return {
        primaryIntent: 'method-call',
        nodeTypesToSearch: ['call_expression', 'method_call', 'member_expression'],
        searchStrategies: ['exact_node', 'parent_traversal', 'span_expansion']
      };
    }

    // Return expressions
    if (reasonLower.includes('return') || reasonLower.includes('implicit return')) {
      return {
        primaryIntent: 'return-expression',
        nodeTypesToSearch: ['return_expression', 'identifier'],
        searchStrategies: ['context_aware', 'position_based', 'parent_traversal']
      };
    }

    // Field access/definition
    if (text.includes(':') && !text.includes('::') || reasonLower.includes('field')) {
      return {
        primaryIntent: 'field-definition',
        nodeTypesToSearch: ['field_expression', 'field_init', 'property'],
        searchStrategies: ['exact_node', 'span_expansion']
      };
    }

    // Comments
    if (text.startsWith('//') || text.startsWith('/*') || reasonLower.includes('comment')) {
      return {
        primaryIntent: 'comment',
        nodeTypesToSearch: ['line_comment', 'block_comment', 'comment'],
        searchStrategies: ['exact_text']
      };
    }

    return {
      primaryIntent: 'unknown',
      nodeTypesToSearch: ['identifier', 'literal', 'expression'],
      searchStrategies: ['text_fallback', 'fuzzy_match']
    };
  }
}

/**
 * AST Node Traversal Engine - Language-agnostic Tree-sitter navigation
 */
export class ASTTraversalEngine {
  /**
   * Find nodes matching LLM selection using Tree-sitter's native AST navigation
   * Uses actual parsed node structure, not text patterns
   */
  static findMatchingNodes(
    targetText: string,
    rootNode: TreeSitterNode,
    searchStrategies: string[]
  ): TreeSitterNode[] {
    const matches: TreeSitterNode[] = [];
    
    for (const strategy of searchStrategies) {
      switch (strategy) {
        case 'exact_node':
          matches.push(...this.findExactTextNodes(targetText, rootNode));
          break;
        case 'parent_traversal':
          matches.push(...this.findNodesWithParentContext(targetText, rootNode));
          break;
        case 'span_expansion':
          matches.push(...this.findExpandedSpanNodes(targetText, rootNode));
          break;
        case 'context_aware':
          matches.push(...this.findContextAwareNodes(targetText, rootNode));
          break;
      }
    }
    
    return this.deduplicateNodes(matches);
  }
  
  /**
   * Find nodes with exact text match using Tree-sitter cursor
   */
  private static findExactTextNodes(targetText: string, rootNode: TreeSitterNode): TreeSitterNode[] {
    const matches: TreeSitterNode[] = [];
    const cursor = rootNode.walk();
    
    const traverse = () => {
      if (cursor.currentNode.text === targetText) {
        matches.push(cursor.currentNode);
      }
      
      if (cursor.gotoFirstChild()) {
        do {
          traverse();
        } while (cursor.gotoNextSibling());
        cursor.gotoParent();
      }
    };
    
    traverse();
    return matches;
  }
  
  /**
   * Find nodes by traversing to parent for complete expressions
   */
  private static findNodesWithParentContext(targetText: string, rootNode: TreeSitterNode): TreeSitterNode[] {
    const matches: TreeSitterNode[] = [];
    const cursor = rootNode.walk();
    
    const traverse = () => {
      const node = cursor.currentNode;
      
      // If this node contains our target text
      if (node.text.includes(targetText)) {
        // Check if parent provides better complete expression
        if (node.parent && this.isMoreCompleteExpression(node.parent.text, node.text, targetText)) {
          matches.push(node.parent);
        } else {
          matches.push(node);
        }
      }
      
      if (cursor.gotoFirstChild()) {
        do {
          traverse();
        } while (cursor.gotoNextSibling());
        cursor.gotoParent();
      }
    };
    
    traverse();
    return matches;
  }
  
  /**
   * Helper methods using Tree-sitter node properties
   */
  private static isMoreCompleteExpression(parentText: string, childText: string, targetText: string): boolean {
    return parentText.length > childText.length && 
           parentText.includes(targetText) && 
           (parentText.includes('(') || parentText.includes('{') || parentText.includes('->'));
  }
  
  private static deduplicateNodes(nodes: TreeSitterNode[]): TreeSitterNode[] {
    const seen = new Set<string>();
    return nodes.filter(node => {
      const key = `${node.startIndex}-${node.endIndex}-${node.type}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }
}

export class TreeSitterQueryEngine {
  private queries: Map<string, TreeSitterQuery> = new Map();
  
  constructor(private tree: TreeSitterTree) {
    this.initializeQueries();
  }

  /**
   * Initialize pre-compiled queries for common patterns
   */
  private initializeQueries(): void {
    // In a real implementation, these would be compiled Tree-sitter queries
    // For now, we'll create a simulation that understands the patterns
    console.log('🔄 Initializing Tree-sitter query patterns...');
    
    // Note: In real Tree-sitter integration, you would do:
    // this.queries.set('method-call', new Query(language, RustQueryPatterns.METHOD_CALL));
  }

  /**
   * Find nodes matching LLM selection with semantic understanding
   */
  findSemanticMatches(
    llmSelection: { text: string; reason: string; diffType: string },
    sourceCode: string
  ): SemanticMatch[] {
    const matches: SemanticMatch[] = [];
    const context = this.determineSemanticContext(llmSelection);
    
    console.log(`🔍 Finding semantic matches for "${llmSelection.text}" (context: ${context})`);

    // ENHANCED: Use Tree-sitter native AST traversal with semantic intent classification
    const intent = SemanticIntentClassifier.classifyIntent(llmSelection.text, llmSelection.reason);
    console.log(`🎯 Semantic intent classified: ${intent.primaryIntent} (strategies: ${intent.searchStrategies.join(', ')})`);
    
    // Use AST traversal engine with real Tree-sitter nodes
    if (this.tree.rootNode && this.tree.rootNode.type !== 'source_file') {
      // Real Tree-sitter parsing available
      const astMatches = ASTTraversalEngine.findMatchingNodes(
        llmSelection.text,
        this.tree.rootNode,
        intent.searchStrategies
      );
      
      if (astMatches.length > 0) {
        console.log(`✅ AST Traversal Engine found ${astMatches.length} matches`);
        const semanticMatches = astMatches.map(node => ({
          node,
          nodeType: node.type,
          context: intent.primaryIntent,
          confidence: this.calculateASTConfidence(node, llmSelection.text),
          completeExpression: this.isCompleteASTExpression(node),
          textCoverage: this.calculateTextCoverage(llmSelection.text, node.text),
          semanticRelevance: this.calculateASTSemanticRelevance(node, intent),
          astTraversal: 'native_ast'
        }));
        
        matches.push(...semanticMatches);
        
        // If we found high-quality AST matches, return them
        const bestMatch = semanticMatches[0];
        if (bestMatch && bestMatch.confidence > 0.8 && bestMatch.textCoverage > 70) {
          return matches.sort((a, b) => b.semanticRelevance - a.semanticRelevance);
        }
      }
    } else {
      // Simulation mode - use enhanced text-based semantic understanding
      console.log('🔄 Using simulation mode with enhanced semantic understanding');
    }

    // Fall back to context-based matching
    switch (context) {
      case 'function-signature':
        matches.push(...this.findFunctionSignatureMatches(llmSelection, sourceCode));
        break;
      case 'method-call':
        matches.push(...this.findMethodCallMatches(llmSelection, sourceCode));
        break;
      case 'return-expression':
        matches.push(...this.findReturnExpressionMatches(llmSelection, sourceCode));
        break;
      case 'field-definition':
        matches.push(...this.findFieldDefinitionMatches(llmSelection, sourceCode));
        break;
      case 'comment':
        matches.push(...this.findCommentMatches(llmSelection, sourceCode));
        break;
      case 'struct-closing':
        matches.push(...this.findStructClosingMatches(llmSelection, sourceCode));
        break;
      default:
        matches.push(...this.findGenericMatches(llmSelection, sourceCode));
    }

    return matches.sort((a, b) => b.semanticRelevance - a.semanticRelevance);
  }

  /**
   * Determine semantic context from LLM selection
   */
  private determineSemanticContext(llmSelection: { text: string; reason: string; diffType: string }): SemanticContext {
    const text = llmSelection.text.toLowerCase();
    const reason = llmSelection.reason.toLowerCase();

    // Function signatures
    if (text.startsWith('fn ') && text.includes('->')) {
      return 'function-signature';
    }

    // Method calls with :: 
    if (text.includes('::')) {
      return 'method-call';
    }

    // Comments
    if (text.startsWith('//')) {
      return 'comment';
    }

    // Field definitions (name: type patterns)
    if (text.includes(':') && !text.includes('::') && text.includes(' ')) {
      return 'field-definition';
    }

    // Struct closing patterns
    if (text.includes('});') || text.includes('}') && text.length <= 5) {
      return 'struct-closing';
    }

    // Return expressions
    if (reason.includes('return') || reason.includes('implicit return')) {
      return 'return-expression';
    }

    return 'unknown';
  }


  /**
   * Find function signature matches
   */
  private findFunctionSignatureMatches(
    llmSelection: { text: string; reason: string },
    sourceCode: string
  ): SemanticMatch[] {
    console.log('🦀 Searching for function signature matches...');
    
    // Extract function name from LLM selection
    const fnMatch = llmSelection.text.match(/fn\s+(\w+)/);
    if (!fnMatch) return [];

    const functionName = fnMatch[1];

    // Use simulated AST navigation to find complete function signatures
    const matches = this.findNodesWithQuery(
      sourceCode,
      (line: string, lineIndex: number) => {
        if (line.includes(`fn ${functionName}`) && line.includes('->')) {
          // Find the complete function signature
          const sigMatch = line.match(/fn\s+\w+[^{]*/);
          if (sigMatch) {
            const startCol = line.indexOf(sigMatch[0]);
            const endCol = startCol + sigMatch[0].length;
            
            return {
              text: sigMatch[0],
              startPosition: { row: lineIndex, column: startCol },
              endPosition: { row: lineIndex, column: endCol },
              type: 'function_item'
            };
          }
        }
        return null;
      }
    );

    return matches.map(node => ({
      node,
      context: 'function-signature' as SemanticContext,
      confidence: this.calculateTextCoverage(llmSelection.text, node.text),
      completeExpression: true,
      textCoverage: this.calculateTextCoverage(llmSelection.text, node.text),
      semanticRelevance: 0.9 // High relevance for exact context match
    }));
  }

  /**
   * Find method call matches with complete expression boundaries
   */
  private findMethodCallMatches(
    llmSelection: { text: string; reason: string },
    sourceCode: string
  ): SemanticMatch[] {
    console.log('🔧 Searching for method call matches...');
    
    // Extract method pattern
    const methodMatch = llmSelection.text.match(/(\w+)::(\w+)/);
    if (!methodMatch) return [];

    const [, className, methodName] = methodMatch;

    const matches = this.findNodesWithQuery(
      sourceCode,
      (line: string, lineIndex: number) => {
        if (line.includes(`${className}::${methodName}`)) {
          // ENHANCED: Try to find complete expression with intelligent boundary detection
          const patterns = [
            // Complete constructor with struct initialization (e.g., Box::new(User {)
            {
              regex: new RegExp(`(${className}::${methodName}\\s*\\([^)]*\\s*[A-Za-z_][A-Za-z0-9_]*\\s*\\{)`),
              priority: 1.0,
              completeExpression: true
            },
            // Method call with string parameters (e.g., String::from("Alice"))
            {
              regex: new RegExp(`(${className}::${methodName}\\s*\\([^)]*\\))`),
              priority: 0.9,
              completeExpression: true
            },
            // Method call with opening parenthesis but incomplete
            {
              regex: new RegExp(`(${className}::${methodName}\\s*\\([^)]*)`),
              priority: 0.8,
              completeExpression: false
            },
            // Just the method part
            {
              regex: new RegExp(`(${className}::${methodName})`),
              priority: 0.6,
              completeExpression: false
            }
          ];

          // Try patterns in priority order
          for (const pattern of patterns) {
            const match = line.match(pattern.regex);
            if (match) {
              const startCol = line.indexOf(match[1]);
              const endCol = startCol + match[1].length;
              
              // Expand to include more context if the LLM selection is longer
              let expandedText = match[1];
              let expandedEndCol = endCol;
              
              if (llmSelection.text.length > match[1].length) {
                // Look for additional context that might complete the expression
                const remainingLine = line.substring(endCol);
                const additionalContext = remainingLine.match(/^[^,;)\}]*/)?.[0] || '';
                if (additionalContext.length > 0) {
                  expandedText = match[1] + additionalContext;
                  expandedEndCol = endCol + additionalContext.length;
                }
              }
              
              return {
                text: expandedText,
                startPosition: { row: lineIndex, column: startCol },
                endPosition: { row: lineIndex, column: expandedEndCol },
                type: 'call_expression',
                priority: pattern.priority,
                completeExpression: pattern.completeExpression
              };
            }
          }
        }
        return null;
      }
    );

    return matches.map(node => ({
      node,
      context: 'method-call' as SemanticContext,
      confidence: this.calculateTextCoverage(llmSelection.text, node.text),
      completeExpression: (node as any).completeExpression || node.text.includes('('),
      textCoverage: this.calculateTextCoverage(llmSelection.text, node.text),
      semanticRelevance: (node as any).priority || (node.text.includes('(') ? 0.9 : 0.7)
    }));
  }

  /**
   * Find return expression matches with context disambiguation
   */
  private findReturnExpressionMatches(
    llmSelection: { text: string; reason: string },
    sourceCode: string
  ): SemanticMatch[] {
    console.log('🔙 Searching for return expression matches...');
    
    const targetText = llmSelection.text.trim();
    const lines = sourceCode.split('\n');
    const matches: SemanticMatch[] = [];

    for (let i = lines.length - 1; i >= 0; i--) {
      const line = lines[i].trim();
      
      // Look for the target text in return context (final expression without semicolon)
      if (line === targetText && !line.endsWith(';')) {
        const fullLine = lines[i];
        const startCol = fullLine.indexOf(targetText);
        const endCol = startCol + targetText.length;

        matches.push({
          node: {
            text: targetText,
            startPosition: { row: i, column: startCol },
            endPosition: { row: i, column: endCol },
            type: 'return_expression'
          } as TreeSitterNode,
          context: 'return-expression',
          confidence: 1.0,
          completeExpression: true,
          textCoverage: 100,
          semanticRelevance: 0.95 // Very high for correct return context
        });
        break; // Found the return expression, stop looking
      }
    }

    return matches;
  }

  /**
   * Find field definition matches
   */
  private findFieldDefinitionMatches(
    llmSelection: { text: string; reason: string },
    sourceCode: string
  ): SemanticMatch[] {
    console.log('🏗️ Searching for field definition matches...');
    
    const fieldMatch = llmSelection.text.match(/(\w+):\s*(\w+)/);
    if (!fieldMatch) return [];

    const [fullPattern, fieldName, fieldType] = fieldMatch;

    const matches = this.findNodesWithQuery(
      sourceCode,
      (line: string, lineIndex: number) => {
        if (line.includes(`${fieldName}:`) && line.includes(fieldType)) {
          const patternIndex = line.indexOf(fullPattern);
          if (patternIndex !== -1) {
            return {
              text: fullPattern,
              startPosition: { row: lineIndex, column: patternIndex },
              endPosition: { row: lineIndex, column: patternIndex + fullPattern.length },
              type: 'field_expression'
            };
          }
        }
        return null;
      }
    );

    return matches.map(node => ({
      node,
      context: 'field-definition' as SemanticContext,
      confidence: 1.0,
      completeExpression: true,
      textCoverage: 100,
      semanticRelevance: 0.8
    }));
  }

  /**
   * Find comment matches
   */
  private findCommentMatches(
    llmSelection: { text: string; reason: string },
    sourceCode: string
  ): SemanticMatch[] {
    const matches = this.findNodesWithQuery(
      sourceCode,
      (line: string, lineIndex: number) => {
        const commentIndex = line.indexOf(llmSelection.text);
        if (commentIndex !== -1) {
          return {
            text: llmSelection.text,
            startPosition: { row: lineIndex, column: commentIndex },
            endPosition: { row: lineIndex, column: commentIndex + llmSelection.text.length },
            type: 'line_comment'
          };
        }
        return null;
      }
    );

    return matches.map(node => ({
      node,
      context: 'comment' as SemanticContext,
      confidence: 1.0,
      completeExpression: true,
      textCoverage: 100,
      semanticRelevance: 1.0 // Exact match for comments
    }));
  }

  /**
   * Find struct closing pattern matches (e.g., "});")
   */
  private findStructClosingMatches(
    llmSelection: { text: string; reason: string },
    sourceCode: string
  ): SemanticMatch[] {
    console.log('🏗️ Searching for struct closing matches...');
    
    const matches = this.findNodesWithQuery(
      sourceCode,
      (line: string, lineIndex: number) => {
        // Look for the exact closing pattern
        const closingIndex = line.indexOf(llmSelection.text);
        if (closingIndex !== -1) {
          return {
            text: llmSelection.text,
            startPosition: { row: lineIndex, column: closingIndex },
            endPosition: { row: lineIndex, column: closingIndex + llmSelection.text.length },
            type: 'struct_closing'
          };
        }
        
        // Also try to find partial matches with context expansion
        if (llmSelection.text.includes('};') || llmSelection.text.includes('}')) {
          const braceIndex = line.indexOf('}');
          if (braceIndex !== -1) {
            // Look for surrounding context
            const beforeBrace = line.substring(Math.max(0, braceIndex - 10), braceIndex).trim();
            const afterBrace = line.substring(braceIndex, Math.min(line.length, braceIndex + 10));
            const contextMatch = beforeBrace + afterBrace;
            
            if (contextMatch.includes(llmSelection.text)) {
              const fullMatchIndex = line.indexOf(llmSelection.text);
              return {
                text: llmSelection.text,
                startPosition: { row: lineIndex, column: fullMatchIndex >= 0 ? fullMatchIndex : braceIndex },
                endPosition: { row: lineIndex, column: fullMatchIndex >= 0 ? fullMatchIndex + llmSelection.text.length : braceIndex + 1 },
                type: 'struct_closing'
              };
            }
          }
        }
        
        return null;
      }
    );

    return matches.map(node => ({
      node,
      context: 'struct-closing' as SemanticContext,
      confidence: 0.9,
      completeExpression: true,
      textCoverage: 100,
      semanticRelevance: 0.8 // High for syntax elements
    }));
  }

  /**
   * Intelligent generic matching with AST-guided fallback
   */
  private findGenericMatches(
    llmSelection: { text: string; reason: string },
    sourceCode: string
  ): SemanticMatch[] {
    console.log('🔧 Using intelligent generic matching with AST guidance...');
    
    const matches: TreeSitterNode[] = [];
    
    // Multi-strategy approach for intelligent fallback
    const strategies = [
      // Strategy 1: Exact text match with context analysis
      this.findExactWithContext(llmSelection, sourceCode),
      // Strategy 2: Fuzzy matching with similarity scoring
      this.findFuzzyMatches(llmSelection, sourceCode),
      // Strategy 3: Semantic hint-based matching
      this.findBySemanticHints(llmSelection, sourceCode),
      // Strategy 4: Pattern-based matching
      this.findPatternBasedMatches(llmSelection, sourceCode)
    ];

    // Flatten and deduplicate results
    const allMatches = strategies.flat().filter(Boolean);
    const deduplicatedMatches = this.deduplicateMatches(allMatches);
    
    return deduplicatedMatches.map(node => ({
      node,
      context: 'unknown' as SemanticContext,
      confidence: this.calculateMatchConfidence(llmSelection, node),
      completeExpression: this.isCompleteExpression(node.text),
      textCoverage: this.calculateTextCoverage(llmSelection.text, node.text),
      semanticRelevance: this.calculateSemanticRelevance(llmSelection, node)
    }));
  }

  /**
   * Find exact matches with surrounding context analysis
   */
  private findExactWithContext(
    llmSelection: { text: string; reason: string },
    sourceCode: string
  ): TreeSitterNode[] {
    const matches: TreeSitterNode[] = [];
    const lines = sourceCode.split('\n');

    lines.forEach((line, lineIndex) => {
      const textIndex = line.indexOf(llmSelection.text);
      if (textIndex !== -1) {
        // Analyze surrounding context to improve match quality
        const beforeText = line.substring(0, textIndex).trim();
        const afterText = line.substring(textIndex + llmSelection.text.length).trim();
        
        matches.push({
          text: llmSelection.text,
          startPosition: { row: lineIndex, column: textIndex },
          endPosition: { row: lineIndex, column: textIndex + llmSelection.text.length },
          startIndex: 0,
          endIndex: llmSelection.text.length,
          type: 'exact_match',
          children: [],
          walk: () => ({} as TreeCursor),
          // Add context metadata
          beforeContext: beforeText,
          afterContext: afterText
        } as any);
      }
    });

    return matches;
  }

  /**
   * Find fuzzy matches with similarity scoring
   */
  private findFuzzyMatches(
    llmSelection: { text: string; reason: string },
    sourceCode: string
  ): TreeSitterNode[] {
    const matches: TreeSitterNode[] = [];
    const lines = sourceCode.split('\n');
    const targetWords = llmSelection.text.toLowerCase().split(/\s+/);

    lines.forEach((line, lineIndex) => {
      const lineWords = line.toLowerCase().split(/\s+/);
      
      // Calculate word overlap
      const overlap = targetWords.filter(word => lineWords.some(lineWord => 
        lineWord.includes(word) || word.includes(lineWord)
      ));
      
      if (overlap.length >= Math.max(1, targetWords.length * 0.5)) {
        // Find the best substring match in this line
        let bestMatch = { start: -1, end: -1, text: '', similarity: 0 };
        
        for (let i = 0; i < line.length - llmSelection.text.length + 1; i++) {
          const candidate = line.substring(i, i + llmSelection.text.length);
          const similarity = this.calculateStringSimilarity(llmSelection.text, candidate);
          
          if (similarity > bestMatch.similarity && similarity > 0.6) {
            bestMatch = {
              start: i,
              end: i + candidate.length,
              text: candidate,
              similarity
            };
          }
        }
        
        if (bestMatch.start >= 0) {
          matches.push({
            text: bestMatch.text,
            startPosition: { row: lineIndex, column: bestMatch.start },
            endPosition: { row: lineIndex, column: bestMatch.end },
            startIndex: 0,
            endIndex: bestMatch.text.length,
            type: 'fuzzy_match',
            children: [],
            walk: () => ({} as TreeCursor),
            similarity: bestMatch.similarity
          } as any);
        }
      }
    });

    return matches;
  }

  /**
   * Find matches based on semantic hints from LLM reason
   */
  private findBySemanticHints(
    llmSelection: { text: string; reason: string },
    sourceCode: string
  ): TreeSitterNode[] {
    const reason = llmSelection.reason.toLowerCase();
    const hints: string[] = [];
    
    // Extract semantic hints from the reason
    if (reason.includes('function') || reason.includes('signature')) hints.push('fn', '->');
    if (reason.includes('heap') || reason.includes('allocation')) hints.push('Box', 'new');
    if (reason.includes('string')) hints.push('String', 'from', '"');
    if (reason.includes('struct') || reason.includes('field')) hints.push(':', '{', '}');
    if (reason.includes('return') || reason.includes('expression')) hints.push('user', 'return');

    const matches: TreeSitterNode[] = [];
    const lines = sourceCode.split('\n');

    lines.forEach((line, lineIndex) => {
      const hintMatches = hints.filter(hint => line.includes(hint));
      if (hintMatches.length >= 2) { // Multiple hints suggest relevance
        // Look for the target text or similar patterns in this line
        const patterns = [
          llmSelection.text,
          llmSelection.text.replace(/\s+/g, '\\s*'), // Flexible whitespace
          ...hints.filter(hint => hint.length > 2) // Longer hints as patterns
        ];
        
        for (const pattern of patterns) {
          try {
            const regex = new RegExp(pattern, 'i');
            const match = line.match(regex);
            if (match) {
              const startCol = line.indexOf(match[0]);
              matches.push({
                text: match[0],
                startPosition: { row: lineIndex, column: startCol },
                endPosition: { row: lineIndex, column: startCol + match[0].length },
                startIndex: 0,
                endIndex: match[0].length,
                type: 'semantic_hint_match',
                children: [],
                walk: () => ({} as TreeCursor),
                hints: hintMatches
              } as any);
              break;
            }
          } catch (e) {
            // Skip invalid regex patterns
          }
        }
      }
    });

    return matches;
  }

  /**
   * Find matches using pattern-based heuristics
   */
  private findPatternBasedMatches(
    llmSelection: { text: string; reason: string },
    sourceCode: string
  ): TreeSitterNode[] {
    const patterns = [
      // Rust-specific patterns based on common constructs
      { pattern: /\b\w+::\w+\s*\([^)]*\)/, type: 'method_call_pattern' },
      { pattern: /fn\s+\w+\s*\([^)]*\)\s*->\s*\w+/, type: 'function_signature_pattern' },
      { pattern: /\w+:\s*\w+,?/, type: 'field_pattern' },
      { pattern: /\/\/[^\n]*/, type: 'comment_pattern' },
      { pattern: /\w+\s*\{[^}]*\}/, type: 'struct_init_pattern' }
    ];

    const matches: TreeSitterNode[] = [];
    const lines = sourceCode.split('\n');

    lines.forEach((line, lineIndex) => {
      for (const { pattern, type } of patterns) {
        const patternMatches = [...line.matchAll(new RegExp(pattern, 'g'))];
        
        patternMatches.forEach(match => {
          if (match[0] && this.isRelevantToSelection(llmSelection.text, match[0])) {
            const startCol = match.index || 0;
            matches.push({
              text: match[0],
              startPosition: { row: lineIndex, column: startCol },
              endPosition: { row: lineIndex, column: startCol + match[0].length },
              startIndex: 0,
              endIndex: match[0].length,
              type: type,
              children: [],
              walk: () => ({} as TreeCursor)
            });
          }
        });
      }
    });

    return matches;
  }

  /**
   * Helper methods for intelligent fallback
   */
  private deduplicateMatches(matches: TreeSitterNode[]): TreeSitterNode[] {
    const seen = new Set<string>();
    return matches.filter(match => {
      const key = `${match.startPosition.row}:${match.startPosition.column}:${match.text}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  private calculateMatchConfidence(llmSelection: { text: string; reason: string }, node: TreeSitterNode): number {
    const textSimilarity = this.calculateStringSimilarity(llmSelection.text, node.text);
    const lengthRatio = Math.min(node.text.length, llmSelection.text.length) / 
                        Math.max(node.text.length, llmSelection.text.length);
    const contextBonus = (node as any).hints?.length ? 0.1 : 0;
    
    return Math.min(1.0, textSimilarity * 0.6 + lengthRatio * 0.3 + contextBonus);
  }

  private isCompleteExpression(text: string): boolean {
    // Heuristics for complete expressions
    const completePatterns = [
      /\([^)]*\)/, // Has parentheses
      /\{[^}]*\}/, // Has braces
      /^\/\//, // Complete comment
      /\w+::\w+/, // Complete method call
      /fn\s+\w+.*->/ // Function signature with return
    ];
    
    return completePatterns.some(pattern => pattern.test(text));
  }

  private calculateSemanticRelevance(llmSelection: { text: string; reason: string }, node: TreeSitterNode): number {
    let relevance = 0.5; // Base relevance
    
    // Boost for exact matches
    if (node.text === llmSelection.text) relevance += 0.4;
    
    // Boost for type-specific matches
    switch (node.type) {
      case 'exact_match': relevance += 0.2; break;
      case 'fuzzy_match': relevance += (node as any).similarity * 0.3; break;
      case 'semantic_hint_match': relevance += 0.25; break;
      case 'method_call_pattern': relevance += 0.2; break;
      case 'function_signature_pattern': relevance += 0.3; break;
    }
    
    return Math.min(1.0, relevance);
  }

  private isRelevantToSelection(selectionText: string, patternMatch: string): boolean {
    const selectionWords = selectionText.toLowerCase().split(/\W+/).filter(Boolean);
    const patternWords = patternMatch.toLowerCase().split(/\W+/).filter(Boolean);
    
    const commonWords = selectionWords.filter(word => 
      patternWords.some(pWord => pWord.includes(word) || word.includes(pWord))
    );
    
    return commonWords.length >= Math.max(1, selectionWords.length * 0.3);
  }

  /**
   * Utility: Find nodes using a query function
   */
  private findNodesWithQuery(
    sourceCode: string,
    queryFn: (line: string, lineIndex: number) => { text: string; startPosition: any; endPosition: any; type: string } | null
  ): TreeSitterNode[] {
    const lines = sourceCode.split('\n');
    const nodes: TreeSitterNode[] = [];

    lines.forEach((line, index) => {
      const result = queryFn(line, index);
      if (result) {
        nodes.push({
          text: result.text,
          startPosition: result.startPosition,
          endPosition: result.endPosition,
          startIndex: 0, // Would be calculated from position
          endIndex: result.text.length,
          type: result.type,
          children: [],
          walk: () => ({} as TreeCursor)
        });
      }
    });

    return nodes;
  }

  /**
   * Calculate how much of the target text is covered by the found node
   */
  private calculateTextCoverage(targetText: string, nodeText: string): number {
    if (nodeText.includes(targetText)) return 100;
    if (targetText.includes(nodeText)) return (nodeText.length / targetText.length) * 100;
    
    // Calculate fuzzy similarity
    const similarity = this.calculateStringSimilarity(targetText, nodeText);
    return similarity * 100;
  }

  /**
   * Calculate string similarity using Levenshtein distance
   */
  private calculateStringSimilarity(str1: string, str2: string): number {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    
    if (longer.length === 0) return 1.0;
    
    const editDistance = this.levenshteinDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  }

  /**
   * Calculate Levenshtein distance between two strings
   */
  private levenshteinDistance(str1: string, str2: string): number {
    const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));
    
    for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
    for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;
    
    for (let j = 1; j <= str2.length; j++) {
      for (let i = 1; i <= str1.length; i++) {
        const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
        matrix[j][i] = Math.min(
          matrix[j][i - 1] + 1,
          matrix[j - 1][i] + 1,
          matrix[j - 1][i - 1] + cost
        );
      }
    }
    
    return matrix[str2.length][str1.length];
  }
  
  /**
   * AST-native helper methods using Tree-sitter node properties
   */
  private calculateASTConfidence(node: TreeSitterNode, targetText: string): number {
    const exactMatch = node.text === targetText ? 0.5 : 0;
    const textSimilarity = this.calculateStringSimilarity(targetText, node.text) * 0.3;
    const nodeTypeBonus = this.getNodeTypeConfidenceBonus(node.type) * 0.2;
    
    return Math.min(1.0, exactMatch + textSimilarity + nodeTypeBonus);
  }
  
  private isCompleteASTExpression(node: TreeSitterNode): boolean {
    // Use Tree-sitter node types to determine completeness
    const completeNodeTypes = [
      'call_expression', 'function_item', 'struct_expression', 
      'return_expression', 'block_comment', 'line_comment'
    ];
    
    return completeNodeTypes.includes(node.type) || 
           (node.children && node.children.length > 0);
  }
  
  private calculateASTSemanticRelevance(
    node: TreeSitterNode, 
    intent: { primaryIntent: SemanticContext; nodeTypesToSearch: string[] }
  ): number {
    let relevance = 0.5; // Base relevance
    
    // Boost for matching node types from semantic classification
    if (intent.nodeTypesToSearch.includes(node.type)) {
      relevance += 0.3;
    }
    
    // Boost for contextually appropriate nodes
    const contextBonus = this.getContextualRelevanceBonus(node, intent.primaryIntent);
    relevance += contextBonus * 0.2;
    
    return Math.min(1.0, relevance);
  }
  
  private getNodeTypeConfidenceBonus(nodeType: string): number {
    const typeConfidence: Record<string, number> = {
      'call_expression': 0.9,
      'function_item': 0.95,
      'struct_expression': 0.8,
      'return_expression': 0.85,
      'identifier': 0.6,
      'field_expression': 0.7,
      'line_comment': 1.0,
      'block_comment': 1.0
    };
    
    return typeConfidence[nodeType] || 0.5;
  }
  
  private getContextualRelevanceBonus(node: TreeSitterNode, context: SemanticContext): number {
    const contextNodeMapping: Record<SemanticContext, string[]> = {
      'function-signature': ['function_item', 'method_definition'],
      'method-call': ['call_expression', 'method_call'],
      'return-expression': ['return_expression', 'identifier'],
      'field-definition': ['field_expression', 'field_init'],
      'struct-initialization': ['struct_expression'],
      'struct-closing': ['punctuation'],
      'comment': ['line_comment', 'block_comment'],
      'variable-declaration': ['identifier', 'variable_declaration'],
      'unknown': []
    };
    
    const relevantTypes = contextNodeMapping[context] || [];
    return relevantTypes.includes(node.type) ? 0.5 : 0;
  }
}

// Tree-sitter Tree interface for compatibility
interface TreeSitterTree {
  rootNode: TreeSitterNode;
}